import * as React from "react";
import { X } from "lucide-react";
import ReactDOM from "react-dom";

import { cn } from "../lib/utils";

/* ------------------------------------------------------------------ */
/*  Context                                                           */
/* ------------------------------------------------------------------ */

interface SheetContextValue {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SheetContext = React.createContext<SheetContextValue>({
  open: false,
  onOpenChange: () => {},
});

function useSheetContext() {
  return React.useContext(SheetContext);
}

/* ------------------------------------------------------------------ */
/*  Sheet (root)                                                      */
/* ------------------------------------------------------------------ */

export function Sheet({
  open = false,
  onOpenChange = () => {},
  children,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <SheetContext.Provider value={{ open, onOpenChange }}>
      {children}
    </SheetContext.Provider>
  );
}

/* ------------------------------------------------------------------ */
/*  SheetTrigger                                                      */
/* ------------------------------------------------------------------ */

export function SheetTrigger({
  asChild,
  children,
}: {
  asChild?: boolean;
  children: React.ReactNode;
}) {
  const { open, onOpenChange } = useSheetContext();

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(
      children as React.ReactElement<Record<string, unknown>>,
      {
        onClick: (e: React.MouseEvent) => {
          const orig = (children as React.ReactElement<Record<string, unknown>>)
            .props.onClick as ((e: React.MouseEvent) => void) | undefined;
          orig?.(e);
          onOpenChange(!open);
        },
      },
    );
  }

  return (
    <button type="button" onClick={() => onOpenChange(!open)}>
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  SheetOverlay                                                      */
/* ------------------------------------------------------------------ */

export function SheetOverlay({ className }: { className?: string }) {
  const { onOpenChange } = useSheetContext();

  return (
    <div
      className={cn(
        "fixed inset-0 z-50 bg-black/80 transition-opacity duration-300",
        className,
      )}
      onClick={() => onOpenChange(false)}
      aria-hidden="true"
    />
  );
}

/* ------------------------------------------------------------------ */
/*  SheetContent                                                      */
/* ------------------------------------------------------------------ */

const sideStyles = {
  right:
    "inset-y-0 right-0 w-3/4 sm:max-w-sm border-l translate-x-full data-[state=open]:translate-x-0",
  left: "inset-y-0 left-0 w-3/4 sm:max-w-sm border-r -translate-x-full data-[state=open]:translate-x-0",
  top: "inset-x-0 top-0 border-b -translate-y-full data-[state=open]:translate-y-0",
  bottom:
    "inset-x-0 bottom-0 border-t translate-y-full data-[state=open]:translate-y-0",
} as const;

type SheetSide = keyof typeof sideStyles;

export function SheetContent({
  className,
  side = "right",
  children,
}: {
  className?: string;
  side?: SheetSide;
  children: React.ReactNode;
}) {
  const { open, onOpenChange } = useSheetContext();

  // Close on Escape
  React.useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onOpenChange]);

  // Prevent body scroll when open
  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [open]);

  if (!open) return null;

  return ReactDOM.createPortal(
    <>
      <SheetOverlay />
      <div
        role="dialog"
        aria-modal="true"
        data-state={open ? "open" : "closed"}
        className={cn(
          "bg-background fixed z-50 flex flex-col gap-4 p-6 shadow-lg transition-transform duration-300 ease-in-out",
          sideStyles[side],
          className,
        )}
      >
        {children}
        <button
          type="button"
          className="ring-offset-background focus:ring-ring absolute top-4 right-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none"
          onClick={() => onOpenChange(false)}
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>
      </div>
    </>,
    document.body,
  );
}

/* ------------------------------------------------------------------ */
/*  SheetClose                                                        */
/* ------------------------------------------------------------------ */

export function SheetClose({
  asChild,
  children,
}: {
  asChild?: boolean;
  children: React.ReactNode;
}) {
  const { onOpenChange } = useSheetContext();

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(
      children as React.ReactElement<Record<string, unknown>>,
      {
        onClick: (e: React.MouseEvent) => {
          const orig = (children as React.ReactElement<Record<string, unknown>>)
            .props.onClick as ((e: React.MouseEvent) => void) | undefined;
          orig?.(e);
          onOpenChange(false);
        },
      },
    );
  }

  return (
    <button type="button" onClick={() => onOpenChange(false)}>
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  Layout wrappers                                                   */
/* ------------------------------------------------------------------ */

export function SheetHeader({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "flex flex-col space-y-2 text-center sm:text-left",
        className,
      )}
      {...props}
    />
  );
}

export function SheetFooter({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
        className,
      )}
      {...props}
    />
  );
}

export function SheetTitle({
  className,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h2
      className={cn("text-foreground text-lg font-semibold", className)}
      {...props}
    />
  );
}

export function SheetDescription({
  className,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p className={cn("text-muted-foreground text-sm", className)} {...props} />
  );
}
